import React, { useEffect, useState } from 'react';
import MultiUploader from '../../MultiUploader';
import { Feedback, useSectionFeedback } from './sectionFeedback';
import { getStorefrontBannerDisplayMode } from '../../../../../../shared/storefrontImagery.js';

const IMAGE_FIELDS = [
    {
        idField: 'backgroundImageId',
        imageField: 'backgroundImage',
        label: 'Background Image',
        help: 'Recommended: at least 1600 × 900 px (16:9). It fills the browser background.',
        previewType: 'background',
    },
    {
        idField: 'profilePictureId',
        imageField: 'profilePicture',
        label: 'Profile Picture',
        help: 'Recommended: at least 600 × 600 px (1:1). It displays as a centered circle.',
        previewType: 'profile',
    },
    {
        idField: 'bannerImageId',
        imageField: 'bannerImage',
        label: 'Banner Image',
        help: 'Recommended: 1800 × 600 px (3:1). Mobile uses a 2:1 center crop.',
        previewType: 'banner',
    },
    {
        idField: 'socialImageId',
        imageField: 'socialImage',
        label: 'Social Share Image',
        help: 'Recommended: 1200 × 630 px (1.91:1). Shown when your storefront link is shared '
            + 'on social media. Falls back to your profile picture, banner, or background image.',
        previewType: 'social',
    },
];

function initialImageIds(basics) {
    return Object.fromEntries(IMAGE_FIELDS.map(({ idField, imageField }) => [
        idField,
        basics[idField] || basics[imageField]?.id || null,
    ]));
}

function initialImages(basics) {
    return Object.fromEntries(IMAGE_FIELDS.map(({ imageField }) => [
        imageField,
        basics[imageField] || null,
    ]));
}

function imageUrl(image, width) {
    const url = image?.cdnUrl || image?.cdnPath;
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}w=${width}`;
}

function BannerCropImage({ image, type, displayMode }) {
    const src = imageUrl(image, type === 'desktop' ? 1200 : 800);
    const label = type === 'desktop' ? 'Desktop banner crop preview' : 'Mobile banner crop preview';
    const frameClasses = [
        'sfm-banner-crop-frame',
        `sfm-banner-crop-frame--${type}`,
        displayMode === 'contain' ? 'sfm-banner-crop-frame--contained' : '',
    ].filter(Boolean).join(' ');

    return (
        <figure className="sfm-crop-preview">
            <div className={frameClasses}>
                {displayMode === 'contain' ? (
                    <img
                        src={src}
                        alt=""
                        aria-hidden="true"
                        className="sfm-banner-crop-backdrop"
                    />
                ) : null}
                <img src={src} alt={label} className="sfm-banner-crop-image" />
            </div>
            <figcaption>
                {type === 'desktop' ? 'Desktop · 3:1' : 'Mobile · 2:1'}
            </figcaption>
        </figure>
    );
}

function ImagePreview({ image, label, previewType }) {
    if (!imageUrl(image, 180)) return null;

    if (previewType === 'profile') {
        return (
            <figure className="sfm-crop-preview sfm-profile-crop-preview">
                <div className="sfm-profile-crop-frame">
                    <img
                        src={imageUrl(image, 600)}
                        alt="Circular profile picture crop preview"
                    />
                </div>
                <figcaption>Storefront avatar · circular 1:1 crop</figcaption>
            </figure>
        );
    }

    if (previewType === 'banner') {
        const displayMode = getStorefrontBannerDisplayMode(image);
        return (
            <div className="sfm-banner-crop-previews">
                <BannerCropImage image={image} type="desktop" displayMode={displayMode} />
                <BannerCropImage image={image} type="mobile" displayMode={displayMode} />
                {displayMode === 'contain' ? (
                    <p className="help sfm-contained-banner-note">
                        This image is unusually narrow or wide, so the storefront keeps the whole
                        image visible over a blurred backdrop.
                    </p>
                ) : null}
            </div>
        );
    }

    return (
        <img
            src={imageUrl(image, 180)}
            alt={`${label} preview`}
            className="sfm-imagery-preview"
        />
    );
}

export default function ImagerySection({ data, saving, onSave, actions }) {
    const basics = data?.basics || {};
    const [imageIds, setImageIds] = useState(() => initialImageIds(basics));
    const [images, setImages] = useState(() => initialImages(basics));
    const { feedback, showFeedback } = useSectionFeedback();

    useEffect(() => {
        setImageIds(initialImageIds(basics));
        setImages(initialImages(basics));
    }, [
        basics.backgroundImageId,
        basics.backgroundImage?.id,
        basics.backgroundImage?.cdnUrl,
        basics.backgroundImage?.cdnPath,
        basics.profilePictureId,
        basics.profilePicture?.id,
        basics.profilePicture?.cdnUrl,
        basics.profilePicture?.cdnPath,
        basics.bannerImageId,
        basics.bannerImage?.id,
        basics.bannerImage?.cdnUrl,
        basics.bannerImage?.cdnPath,
        basics.bannerImage?.metadata?.image?.width,
        basics.bannerImage?.metadata?.image?.height,
        basics.bannerImage?.metadata?.image?.originalWidth,
        basics.bannerImage?.metadata?.image?.originalHeight,
        basics.socialImageId,
        basics.socialImage?.id,
        basics.socialImage?.cdnUrl,
        basics.socialImage?.cdnPath,
    ]);

    function save(payload, successMessage) {
        onSave(async () => {
            const result = await actions.saveImagery(payload);
            showFeedback(result.success ? 'success' : 'error', result.success
                ? successMessage
                : result.error);
            return result;
        });
    }

    function handleSave() {
        save(imageIds, 'Imagery saved.');
    }

    function handleRemove(idField, imageField, label) {
        setImageIds(current => ({ ...current, [idField]: null }));
        setImages(current => ({ ...current, [imageField]: null }));
        save({ [idField]: null }, `${label} removed.`);
    }

    return (
        <div>
            <div className="sfm-section-header">
                <h2>Imagery</h2>
                <p>Upload the images associated with your storefront.</p>
                <p className="help">
                    Profile pictures and banners appear in your public storefront masthead.
                </p>
            </div>
            {IMAGE_FIELDS.map(({ idField, imageField, label, help, previewType }) => {
                const currentImage = images[imageField];
                return (
                    <div className="group sfm-imagery-group" key={idField}>
                        <label>{label}</label>
                        <MultiUploader
                            name={idField}
                            maxFiles={1}
                            accept="image/jpeg,image/png,image/webp"
                            onUploadSuccess={(files) => {
                                const nextImage = files[0];
                                const nextImageId = nextImage?.id;
                                if (nextImageId) {
                                    setImageIds(current => ({
                                        ...current,
                                        [idField]: nextImageId,
                                    }));
                                    setImages(current => ({
                                        ...current,
                                        [imageField]: nextImage,
                                    }));
                                }
                            }}
                        />
                        <p className="help">{help} JPEG, PNG, or WebP.</p>
                        {currentImage ? (
                            <div className="sfm-current-image">
                                <p>{label} preview</p>
                                <ImagePreview
                                    image={currentImage}
                                    label={label}
                                    previewType={previewType}
                                />
                                <button
                                    type="button"
                                    className="button secondary small"
                                    onClick={() => handleRemove(idField, imageField, label)}
                                    disabled={saving}
                                >
                                    Remove {label.toLowerCase()}
                                </button>
                            </div>
                        ) : null}
                    </div>
                );
            })}
            <div className="sfm-save-row">
                <button
                    type="button"
                    className="button primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    Save Imagery
                </button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
